'use client';

/**
 * VendorListCard — expandable vendor tree (explorer).
 *
 * Mode4-native parallel paint of the legacy `<VendorsExplorer />`.
 * Three-level tree: Vendor → Collection → Product. Each level
 * lazy-expands; vendor expansion fires `vendor_explore` for that
 * vendor only (per-row cache keyed on semantic_id, so React Query
 * dedups across remounts).
 *
 * Click semantics (mirrors legacy):
 *   - Vendor row     → opens/activates vendor tab; selects vendor
 *   - Vendor chevron → toggles expand (no canvas change)
 *   - Collection row → opens vendor tab + sets selectedCollectionId
 *                      (canvas drill-in)
 *   - Collection chev → toggles expand to show products
 *   - Product row    → selects product node (rail re-renders)
 *
 * Cards-everywhere doctrine: doesn't import VendorsExplorer or any
 * of its tree-item subcomponents.
 *
 * @see klyde_edge/ui_config/composition/cards/vendors.py — content_key="vendor_list"
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain explorer
 */

import { useMemo, useState } from 'react';
import {
  Loader2,
  XCircle,
  Package,
  Plus,
  ChevronRight,
  Home,
  MoreVertical,
  ClipboardCopy,
  Download,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useVendors, useVendorExplore } from '@/lib/api/hooks';
import {
  CardPlacementProvider,
  useCardExport,
  downloadCapture,
  waitForRailSelection,
} from '@/lib/design/card-export';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useTabStore } from '@/lib/stores/tab-store';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { useLayoutStore } from '@/lib/stores/layout-store';
import { useAffordance, useProductLabel } from '@/lib/stores/total-matrix-store';
import { useLanguageStore } from '@/lib/stores/language-store';
import { cn } from '@/lib/utils/cn';
import { text, treeDepth as depthRamp, surfaceBase } from '@/lib/design/tokens';
import type { CollectionDetail, ProductSummary, VendorSummary } from '@/lib/api/types';

export function VendorListCard() {
  // Layer-1 chrome (server-driven). No literal English in the TSX.
  const labelSection = useProductLabel('vendor_section');
  const labelHome = useProductLabel('vendor_home');
  const labelAdd = useProductLabel('vendor_add');
  const labelEmpty = useProductLabel('vendor_empty');
  const labelLoading = useProductLabel('vendor_loading');
  const labelLoadError = useProductLabel('vendor_load_error');
  const vendorsStrategy = useCapabilityStore((s) =>
    s.strategyName('graph_explore', 'vendor_list'),
  );
  const canAddVendor = useCapabilityStore(
    (s) => s.strategyName('vendor_portal', 'create') !== null,
  );
  const setExplorerSelection = useLayoutStore((s) => s.setExplorerSelection);
  const setActiveTab = useTabStore((s) => s.setActiveTab);
  const activeTabId = useTabStore((s) => s.activeTabId);
  const clearSelection = useGraphSelectionStore((s) => s.clearSelection);

  const { data, isLoading, error } = useVendors(vendorsStrategy ?? '', { limit: 50 });
  const vendors = data?.vendors ?? [];

  // The roster query backs every vendor card's content; shared across
  // instances so the envelope dedups to one payload. Matches useVendors'
  // key shape (['vendors', strategy, params]).
  const rosterQueryKey = useMemo(
    () => ['vendors', vendorsStrategy ?? '', { limit: 50 }],
    [vendorsStrategy],
  );

  // Collection card export — the vendor roster (which vendors exist).
  // The per-vendor children register their own. Called before the early
  // returns per Rules of Hooks.
  useCardExport(useMemo(() => ({
    label: labelSection,
    content_key: 'vendor_list',
    surface: 'explorer' as const,
    status: (isLoading ? 'loading' : 'loaded') as 'loading' | 'loaded',
    query_key: rosterQueryKey,
    text: vendors.map((v) => v.name).join('\n'),
    json: {
      count: vendors.length,
      vendors: vendors.map((v) => ({
        vendor_id: v.semantic_id,
        name: v.name,
        source: v.source,
        collections: v.collection_count,
        products: v.product_count,
      })),
    },
  }), [vendors, isLoading, rosterQueryKey, labelSection]));

  const handleAddVendor = () => setExplorerSelection('add_vendor');
  const handleGoHome = () => {
    setActiveTab(null);
    clearSelection();
    setExplorerSelection(null);
  };

  if (isLoading) {
    return (
      <div style={{ fontSize: surfaceBase.canvas }} className={cn(text.muted, 'flex items-center gap-2 px-3 py-2')}>
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        {labelLoading}
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ fontSize: surfaceBase.canvas }} className={cn(text.body, 'px-3 py-2 flex items-center gap-2 text-destructive')}>
        <XCircle className="h-3.5 w-3.5" />
        {labelLoadError}
      </div>
    );
  }

  return (
    <div style={{ fontSize: surfaceBase.canvas }} className="space-y-1">
      <div className="flex items-center justify-between gap-2 px-2">
        <div className={cn(text.heading, 'flex items-center gap-1.5')}>
          <Package className="h-3.5 w-3.5" />
          {labelSection}
        </div>
        <div className="flex items-center gap-1">
          {activeTabId && (
            <button
              onClick={handleGoHome}
              className={cn(text.body, 'flex items-center gap-1 px-1.5 py-0.5 text-muted-foreground hover:text-foreground hover:bg-accent/50 rounded transition-colors')}
            >
              <Home className="h-3 w-3" />
              {labelHome}
            </button>
          )}
          {canAddVendor && (
            <button
              onClick={handleAddVendor}
              className={cn(text.body, 'flex items-center gap-1 px-1.5 py-0.5 text-primary hover:bg-accent/50 rounded transition-colors')}
            >
              <Plus className="h-3 w-3" />
              {labelAdd}
            </button>
          )}
        </div>
      </div>

      {/* "My Favourites" is a standalone, self-fetching explorer card
          (favourites_explorer) mounted as a peer above this card in the mode's
          explorer_cards — reused by mode4 + mode11. It is NOT composed into the
          vendor roster (favourites aren't vendors). See favourites-explorer-card.tsx. */}

      {vendors.length === 0 ? (
        <div className={cn(text.muted, 'px-3 py-2')}>{labelEmpty}</div>
      ) : (
        <ul className="space-y-1">
          {vendors.map((v) => (
            // Each vendor is a card, generated from the roster. Per-instance
            // slot_id keeps the same-kind cards distinct in the envelope.
            <li key={v.semantic_id}>
              <CardPlacementProvider
                value={{ surface: 'explorer', slot_id: `vendor:${v.semantic_id}` }}
              >
                <VendorRow vendor={v} rosterQueryKey={rosterQueryKey} />
              </CardPlacementProvider>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function VendorRow({
  vendor,
  rosterQueryKey,
}: {
  vendor: VendorSummary;
  rosterQueryKey: readonly unknown[];
}) {
  // Tree depth-ramp: the vendor is the root (depth 0). Name size/weight
  // come from the absolute depthRamp lookup applied to the NAME element;
  // counts/kebab/states use the em `text.*` styles off the surface base,
  // so they stay uniform regardless of depth.
  const depthClass = depthRamp[0];
  const [expanded, setExpanded] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const openTab = useTabStore((s) => s.openTab);
  const updateTab = useTabStore((s) => s.updateTab);
  const activeTabId = useTabStore((s) => s.activeTabId);
  const selectNode = useGraphSelectionStore((s) => s.selectNode);
  const setExplorerSelection = useLayoutStore((s) => s.setExplorerSelection);
  const activeExplorerView = useLayoutStore((s) => s.activeExplorerView);
  const exploreStrategy = useCapabilityStore((s) =>
    s.strategyName('graph_explore', 'vendor_explore'),
  );
  const queryClient = useQueryClient();
  const lang = useLanguageStore((s) => s.lang);

  // Kebab labels (Layer-1, server-driven) + role-gated affordances. The
  // matrix ships only the affordances this actor's role may see; undefined
  // = not in projection = don't render. capture_scene re-enforces at the
  // auth boundary regardless.
  const menuLabel = useProductLabel('vendor_menu_label');
  const copyLabel = useProductLabel('action_copy_vendor');
  const downloadCaptureLabel = useProductLabel('action_download_capture');
  const labelLoadingChildren = useProductLabel('vendor_loading_children');
  const copyTestId = useAffordance('explorer.vendor.copy_report');
  const downloadTestId = useAffordance('explorer.vendor.download_capture');

  const tabId = `vendor-${vendor.semantic_id}`;
  const isActive = activeTabId === tabId;
  const hasChildren = vendor.collection_count > 0 || vendor.product_count > 0;

  // Only fetch when expanded — same lazy pattern legacy uses, so the
  // initial render of a 50-vendor list doesn't fan out to 50 explore
  // calls.
  const { data: exploreData, isLoading } = useVendorExplore(
    exploreStrategy ?? '',
    expanded ? { vendor_id: vendor.semantic_id } : null,
  );

  const openVendor = (collectionId?: string | null) => {
    openTab({
      id: tabId,
      type: 'vendor',
      title: vendor.name,
      entityId: vendor.semantic_id,
      selectedCollectionId: collectionId ?? null,
    });
    // openTab dedups so updateTab is the writer for selectedCollectionId
    // when the tab already existed.
    updateTab(tabId, { selectedCollectionId: collectionId ?? null });
    selectNode({
      id: vendor.semantic_id,
      label: vendor.name,
      type: 'vendor',
      properties: {
        email: vendor.email,
        collection_count: vendor.collection_count,
        product_count: vendor.product_count,
      },
    });
    // Clear any active sub-mode (e.g. add_vendor form) so the canvas
    // swaps from the form back to the catalog for the picked vendor.
    setExplorerSelection(null);
  };

  const toggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (hasChildren) setExpanded((v) => !v);
  };

  // Scene export — this vendor as a card. The collection/product
  // hierarchy is the inline leaf tree (lazy on expand), riding in
  // json.tree. surface + per-instance slot_id come from the parent's
  // CardPlacementProvider; query_key is the shared roster.
  const fmtPrice = (p: number | null | undefined) =>
    p != null ? `$${p.toLocaleString()}` : null;
  // Hoisted so the kebab's Copy reuses the exact markdown this card
  // registered — clipboard == export by construction.
  const exportValue = useMemo(() => {
    const collections = exploreData?.vendor?.collections ?? [];
    const uncategorized = exploreData?.vendor?.uncategorized_products ?? [];
    const metaLine = vendor.source === 'buyer'
      ? `${vendor.product_count} wishes`
      : `${vendor.collection_count}c · ${vendor.product_count}p`;
    return {
      label: vendor.name,
      content_key: 'vendor',
      // Vendor rows sit at the tree root — depth 0. The name renders via
      // the client-side depthRamp[0] lookup now; this field stays on the
      // capture for the (still-shipping) tree_depth bundle signature until
      // the backend engine is removed (then the dump goes silent on it).
      treeDepth: 0,
      status: 'loaded' as const,
      query_key: rosterQueryKey,
      text: `${vendor.name} — ${metaLine}`,
      markdown: [
        `### ${vendor.name}`,
        metaLine,
        ...(expanded
          ? collections.map((c) => `- ${c.name} (${c.product_count})`)
          : []),
      ].join('\n'),
      json: {
        vendor_id: vendor.semantic_id,
        name: vendor.name,
        source: vendor.source,
        collection_count: vendor.collection_count,
        product_count: vendor.product_count,
        tree: expanded
          ? {
              collections: collections.map((c) => ({
                collection_id: c.semantic_id,
                name: c.name,
                product_count: c.product_count,
                products: c.products.map((p) => ({
                  product_id: p.semantic_id, name: p.name, price: fmtPrice(p.list_price),
                })),
              })),
              uncategorized: uncategorized.map((p) => ({
                product_id: p.semantic_id, name: p.name, price: fmtPrice(p.list_price),
              })),
            }
          : null,
      },
      state: { expanded, active: isActive },
    };
  }, [vendor, expanded, exploreData, rosterQueryKey, isActive]);
  useCardExport(exportValue);

  // Copy this vendor (not the whole surface) — reuse the registered
  // markdown so clipboard == export.
  const handleCopy = async () => {
    setMenuOpen(false);
    try {
      await navigator.clipboard.writeText(exportValue.markdown || exportValue.text);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[VendorRow] copy failed:', err);
    }
  };

  // Select-then-capture: if this vendor isn't the active tab, open it
  // (paints the canvas catalog + rail trio) and wait for the rail to
  // settle on this vendor before capturing — the rail keys on the
  // vendor sid, a reliable per-vendor signal. Already-active vendors
  // capture the current scene (including any catalog drill-in).
  // Strategy-only download; the error is diagnostic.
  const handleDownloadCapture = async () => {
    setMenuOpen(false);
    if (!isActive) {
      openVendor();
      await waitForRailSelection(vendor.semantic_id, 5000);
    }
    try {
      await downloadCapture(queryClient, {
        mode: activeExplorerView ?? null,
        selection: vendor.semantic_id,
        resolved_selection: vendor.semantic_id,
        selection_source: 'explicit',
        viewer_lang: lang ?? 'en',
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[VendorRow] capture failed:', err);
    }
  };

  return (
    <div
      className={cn(
        'relative rounded-lg border bg-card transition-colors',
        isActive ? 'border-primary/30 bg-primary/5' : 'border-border hover:bg-accent/30',
      )}
    >
      <div
        onClick={() => openVendor()}
        className="group flex items-center gap-2 px-2 py-1.5 cursor-pointer"
      >
        {hasChildren ? (
          <button
            onClick={toggleExpand}
            className="p-0.5 hover:bg-muted rounded shrink-0"
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            <ChevronRight
              className={cn(
                'h-3.5 w-3.5 text-muted-foreground transition-transform',
                expanded && 'rotate-90',
              )}
            />
          </button>
        ) : (
          <span className="w-4 shrink-0" />
        )}
        <span className={cn(depthClass, 'truncate flex-1', isActive && 'text-primary font-medium')}>
          {vendor.name}
        </span>
        {vendor.product_count > 0 && (
          <span className={cn(text.muted, 'shrink-0 opacity-0 group-hover:opacity-100 transition-opacity')}>
            {vendor.source === 'buyer'
              ? `${vendor.product_count} wish${vendor.product_count !== 1 ? 'es' : ''}`
              : `${vendor.collection_count}c · ${vendor.product_count}p`}
          </span>
        )}
        {/* Kebab — per-vendor actions. Present only when this actor's
            role projection carries at least one action (capture is
            admin-only; copy is unrestricted). */}
        {(copyTestId || downloadTestId) && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((v) => !v);
            }}
            aria-label={menuLabel}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            className="p-1 rounded shrink-0 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors opacity-0 group-hover:opacity-100 aria-expanded:opacity-100"
          >
            <MoreVertical className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {menuOpen && (copyTestId || downloadTestId) && (
        <>
          {/* Click-outside backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setMenuOpen(false)}
            aria-hidden="true"
          />
          <div
            role="menu"
            className="absolute right-1 top-10 z-50 w-52 rounded-md border border-border bg-popover shadow-md py-1"
          >
            {copyTestId && (
              <button
                type="button"
                role="menuitem"
                data-testid={copyTestId}
                onClick={handleCopy}
                className={cn(text.body, 'w-full flex items-center gap-2 px-3 py-1.5 hover:bg-accent text-left')}
              >
                <ClipboardCopy className="w-3.5 h-3.5" />
                {copyLabel}
              </button>
            )}
            {downloadTestId && (
              <button
                type="button"
                role="menuitem"
                data-testid={downloadTestId}
                onClick={() => void handleDownloadCapture()}
                className={cn(text.body, 'w-full flex items-center gap-2 px-3 py-1.5 hover:bg-accent text-left')}
              >
                <Download className="w-3.5 h-3.5" />
                {downloadCaptureLabel}
              </button>
            )}
          </div>
        </>
      )}

      {expanded && (
        <div className="ml-3 mb-1 pl-1 border-l border-border/50">
          {isLoading ? (
            <div className={cn(text.muted, 'flex items-center gap-2 px-3 py-1')}>
              <Loader2 className="h-3 w-3 animate-spin" />
              {labelLoadingChildren}
            </div>
          ) : (
            <>
              {exploreData?.vendor?.collections.map((c) => (
                <CollectionRow
                  key={c.semantic_id}
                  collection={c}
                  onOpen={() => openVendor(c.semantic_id)}
                />
              ))}
              {exploreData?.vendor?.uncategorized_products.map((p) => (
                <ProductLeaf key={p.semantic_id} product={p} />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function CollectionRow({
  collection,
  onOpen,
}: {
  collection: CollectionDetail;
  onOpen: () => void;
}) {
  // Collection sits one level under the vendor — depth 1.
  const depthClass = depthRamp[1];
  const [expanded, setExpanded] = useState(false);
  const toggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded((v) => !v);
  };

  return (
    <div>
      <div
        onClick={onOpen}
        className="group flex items-center gap-1 px-2 py-1 cursor-pointer hover:bg-accent/50 rounded pl-3"
      >
        <button
          onClick={toggleExpand}
          className="p-0.5 hover:bg-muted rounded shrink-0"
          aria-label={expanded ? 'Collapse' : 'Expand'}
        >
          <ChevronRight
            className={cn(
              'h-3 w-3 transition-transform',
              expanded && 'rotate-90',
            )}
          />
        </button>
        <span className={cn(depthClass, 'truncate flex-1')}>{collection.name}</span>
        <span className={cn(text.muted, 'shrink-0 opacity-0 group-hover:opacity-100 transition-opacity')}>
          {collection.product_count}
        </span>
      </div>

      {expanded && (
        <div className="ml-3 border-l border-border/50">
          {collection.products.map((p) => (
            <ProductLeaf key={p.semantic_id} product={p} />
          ))}
        </div>
      )}
    </div>
  );
}

function ProductLeaf({ product }: { product: ProductSummary }) {
  // Product is the leaf — depth 2 (the ramp mutes + lightens it).
  const depthClass = depthRamp[2];
  const selectNode = useGraphSelectionStore((s) => s.selectNode);

  return (
    <button
      onClick={() =>
        selectNode({
          id: product.semantic_id,
          label: product.name,
          type: 'product',
          properties: {
            list_price: product.list_price,
            image_url: product.image_url,
          },
        })
      }
      className="w-full flex items-center gap-1 px-2 py-1 hover:bg-accent/50 rounded pl-4 text-left"
    >
      <span className={cn(depthClass, 'truncate flex-1')}>{product.name}</span>
      {product.list_price != null && (
        <span className={cn(text.muted, 'shrink-0')}>
          ${product.list_price.toLocaleString()}
        </span>
      )}
    </button>
  );
}
